// NewsletterSubscribers.js
import { useEffect, useState } from "react";
import Api from "../../../../shared/api/api";
import { ArrowUp, ArrowDown, ArrowUpDown, Trash2 } from "lucide-react";
import { cycleSort, sortRows } from "../../helpers/tableSorting";
import "../styles/AdminNewsletterSubscribers.css";

function formatDate(value) {
  if (!value) return "Unknown";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString();
  } catch {
    return value;
  }
}

export default function NewsletterSubscribers() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort, setSort] = useState(null);
  const [busy, setBusy] = useState(false);
  const filtered = subscribers.filter(subscriber => !statusFilter || subscriber.status === statusFilter);
  const visible = sortRows(filtered, sort, ["id", "sent_count", "open_count", "click_count"], ["subscribed_at"]);
  function column(key, label) {
    const direction = sort?.key === key ? sort.direction : "none";
    const Icon = direction === "ascending" ? ArrowUp : direction === "descending" ? ArrowDown : ArrowUpDown;
    return <th aria-sort={direction}><button type="button" onClick={() => setSort(previous => cycleSort(previous, key))} title={`Sort ${label}`} className="subscriber-sort"><span>{label}</span><Icon size={15} /></button></th>;
  }

  useEffect(() => {
    fetchSubscribers();
  }, []);

  async function fetchSubscribers() {
    setLoading(true);
    setError("");
    try {
      // GET /newsletter -> full list of subscribers
      const data = await Api.getNewsletterSubscribers();
      const list = Array.isArray(data)
        ? data
        : data.subscribers || data.rows || [];
      setSubscribers(list);
    } catch (err) {
      console.error("Error loading subscribers:", err);
      setError("Failed to load subscribers.");
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id) {
    if (id <= 2) {
      alert("IDs 1 and 2 are reserved and cannot be deleted.");
      return;
    }
    if (!window.confirm("Delete this subscriber? This cannot be undone.")) {
      return;
    }
    try {
      setBusy(true);
      await Api.deleteNewsletterSubscriber(id);
      setSubscribers((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error("Error deleting subscriber:", err);
      alert("Failed to delete subscriber.");
    } finally { setBusy(false); }
  }

  async function handlePruneInactive() {
    if (!window.confirm("Delete ALL inactive subscribers with id > 2?")) {
      return;
    }
    try {
      setBusy(true);
      const result = await Api.pruneInactiveSubscribers();
      setStatusMessage(
        result.message || "Inactive subscribers deleted (id > 2)."
      );
      await fetchSubscribers();
    } catch (err) {
      console.error("Error pruning subscribers:", err);
      alert("Failed to prune inactive subscribers.");
    } finally { setBusy(false); }
  }

  return (
    <div className="adminPageContainer newsletterSubscribers">
      <div className="adminCard">
        <div className="adminHeaderRow">
          <h1>Newsletter Subscribers</h1>
          <button
            type="button"
            className="btnDanger"
            onClick={handlePruneInactive}
            disabled={busy || loading || !subscribers.some(subscriber => subscriber.status === "inactive" && Number(subscriber.id) > 2)}
          >
            Delete Inactive Subscribers
          </button>
        </div>
        <label>Status filter<select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}><option value="">All</option><option value="active">Active</option><option value="inactive">Inactive</option></select></label>

        {statusMessage && (
          <div className="adminStatus">{statusMessage}</div>
        )}
        {error && <div className="adminError">{error}</div>}
        {loading && <div className="adminLoading">Loading...</div>}

        {!loading && (
          <div className="adminTableWrapper">
            <table className="adminTable">
              <thead>
                <tr>
                  {column("id", "ID")}
                  {column("email", "Email")}
                  {column("status", "Status")}
                  {column("subscribed_at", "Subscribed At")}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="adminTableEmpty">
                      No subscribers found.
                    </td>
                  </tr>
                ) : (
                  visible.map((s) => (
                    <tr key={s.id}>
                      <td>{s.id}</td>
                      <td>{s.email}</td>
                      <td className={`status-${s.status || "unknown"}`}>
                        {s.status || "unknown"}
                      </td>
                      <td>{formatDate(s.subscribed_at)}</td>
                      <td>
                        <div className="adminTableActions">
                          <button
                            type="button"
                            className="btnDanger"
                            onClick={() => handleDelete(s.id)}
                            disabled={busy || Number(s.id) <= 2}
                            aria-label={`Delete subscriber ${s.id}`}
                            title="Delete subscriber"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
